import React, { useState } from 'react';
import { Award, Search, Plus, X } from 'lucide-react';

const CredlyBadges = ({ badges = [], onBadgesUpdate }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newBadge, setNewBadge] = useState({
    name: '',
    issuer: '',
    issueDate: '',
    credentialId: '',
    credentialUrl: '',
    imageUrl: ''
  });

  // Popular Credly badges for search
  const popularBadges = [
    { name: 'AWS Certified Solutions Architect', issuer: 'Amazon Web Services' },
    { name: 'Google Cloud Professional', issuer: 'Google Cloud' },
    { name: 'Microsoft Certified: Azure Fundamentals', issuer: 'Microsoft' },
    { name: 'Certified Kubernetes Administrator', issuer: 'Cloud Native Computing Foundation' },
    { name: 'Salesforce Certified Administrator', issuer: 'Salesforce' },
    { name: 'PMP Certification', issuer: 'Project Management Institute' },
    { name: 'Cisco Certified Network Associate', issuer: 'Cisco' },
    { name: 'CompTIA Security+', issuer: 'CompTIA' }
  ];

  const handleAddBadge = () => {
    if (newBadge.name && newBadge.issuer) {
      const badge = {
        ...newBadge,
        id: Date.now(),
        imageUrl: newBadge.imageUrl || `https://images.credly.com/images/${encodeURIComponent(newBadge.name)}.png`
      };
      const updated = [...badges, badge];
      if (onBadgesUpdate) {
        onBadgesUpdate(updated);
      }
      setNewBadge({
        name: '',
        issuer: '',
        issueDate: '',
        credentialId: '',
        credentialUrl: '',
        imageUrl: ''
      });
      setShowAddModal(false);
    }
  };

  const handleRemoveBadge = (badgeId) => {
    const updated = badges.filter(b => b.id !== badgeId);
    if (onBadgesUpdate) {
      onBadgesUpdate(updated);
    }
  };

  const filteredBadges = popularBadges.filter(badge =>
    badge.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    badge.issuer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Award className="w-5 h-5 text-primary-600" />
          Credly Certifications & Badges
        </h3>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn btn-primary text-sm inline-flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Add Badge</span>
        </button>
      </div>

      {/* Badge Display */}
      {badges.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {badges.map(badge => (
            <div
              key={badge.id}
              className="relative border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow group"
            >
              {badge.imageUrl && (
                <img
                  src={badge.imageUrl}
                  alt={badge.name}
                  className="w-full h-32 object-contain mb-2"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/200x200?text=Badge';
                  }}
                />
              )}
              <h4 className="font-semibold text-sm text-gray-900 mb-1 line-clamp-2">
                {badge.name}
              </h4>
              <p className="text-xs text-gray-600 mb-1">{badge.issuer}</p>
              {badge.issueDate && (
                <p className="text-xs text-gray-500">{badge.issueDate}</p>
              )}
              <button
                onClick={() => handleRemoveBadge(badge.id)}
                className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 bg-red-500 text-white rounded-full p-1 transition-opacity"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add Badge Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">Add Certification Badge</h3>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Search Credly */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search Credly Badges
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search for certifications..."
                    className="input pl-10"
                  />
                </div>
                {searchTerm && (
                  <div className="mt-2 border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                    {filteredBadges.map((badge, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          setNewBadge({
                            ...newBadge,
                            name: badge.name,
                            issuer: badge.issuer
                          });
                          setSearchTerm('');
                        }}
                        className="w-full text-left px-4 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                      >
                        <p className="font-medium text-sm">{badge.name}</p>
                        <p className="text-xs text-gray-500">{badge.issuer}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Manual Entry Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Badge Name *
                  </label>
                  <input
                    type="text"
                    value={newBadge.name}
                    onChange={(e) => setNewBadge({ ...newBadge, name: e.target.value })}
                    className="input"
                    placeholder="e.g., AWS Certified Solutions Architect"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Issuing Organization *
                  </label>
                  <input
                    type="text"
                    value={newBadge.issuer}
                    onChange={(e) => setNewBadge({ ...newBadge, issuer: e.target.value })}
                    className="input"
                    placeholder="e.g., Amazon Web Services"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Issue Date
                    </label>
                    <input
                      type="date"
                      value={newBadge.issueDate}
                      onChange={(e) => setNewBadge({ ...newBadge, issueDate: e.target.value })}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Credential ID
                    </label>
                    <input
                      type="text"
                      value={newBadge.credentialId}
                      onChange={(e) => setNewBadge({ ...newBadge, credentialId: e.target.value })}
                      className="input"
                      placeholder="Optional"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Credential URL (Credly link)
                  </label>
                  <input
                    type="url"
                    value={newBadge.credentialUrl}
                    onChange={(e) => setNewBadge({ ...newBadge, credentialUrl: e.target.value })}
                    className="input"
                    placeholder="https://www.credly.com/users/..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Badge Image URL
                  </label>
                  <input
                    type="url"
                    value={newBadge.imageUrl}
                    onChange={(e) => setNewBadge({ ...newBadge, imageUrl: e.target.value })}
                    className="input"
                    placeholder="Leave empty to auto-generate"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleAddBadge}
                  className="btn btn-primary flex-1"
                >
                  Add Badge
                </button>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CredlyBadges;

